import React, { useEffect, useState } from 'react';
import { Alert, Button, Group, Text } from '@mantine/core';
import { IconBell, IconBellCheck, IconShare2 } from '@tabler/icons-react';
import {
  notificationPermission,
  pushRequiresInstall,
  pushSupported,
  subscribeToPush,
  syncExistingSubscription,
} from '../services/pushNotifications';

interface PushNotificationPromptProps {
  gameKey: string;
}

const DISMISSED_KEY = 'pushPromptDismissed';

// Offers turn notifications: a subscribe button where push is available,
// or install-to-home-screen guidance on iOS Safari (where push only works
// after installing). The guidance points at the *next* game because the
// installed app has storage separate from Safari: opening this game there
// would not know the player and invite a duplicate join that stalls stacks.
// Renders nothing once enabled or dismissed.
const PushNotificationPrompt: React.FC<PushNotificationPromptProps> = ({ gameKey }) => {
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISSED_KEY) === 'true');
  const [subscribed, setSubscribed] = useState(false);
  const [justEnabled, setJustEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    syncExistingSubscription(gameKey)
      .then((synced) => {
        if (!cancelled) setSubscribed(synced);
      })
      .catch((e) => console.warn('Could not register push subscription for this game:', e));
    return () => {
      cancelled = true;
    };
  }, [gameKey]);

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    setDismissed(true);
  };

  const handleEnable = async () => {
    setLoading(true);
    setError(null);
    try {
      await subscribeToPush(gameKey);
      setSubscribed(true);
      setJustEnabled(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not enable notifications');
    } finally {
      setLoading(false);
    }
  };

  if (dismissed) return null;

  if (subscribed) {
    if (!justEnabled) return null;
    return (
      <Alert color="gamePrimary" icon={<IconBellCheck size={18} />} radius="md">
        You&apos;ll get a notification when it&apos;s your turn.
      </Alert>
    );
  }

  if (pushRequiresInstall()) {
    return (
      <Alert
        color="gamePrimary"
        icon={<IconShare2 size={18} />}
        radius="md"
        withCloseButton
        onClose={handleDismiss}
        title="Want turn notifications next time?"
      >
        <Text size="sm">
          On iPhone they only work in the home-screen app. Before your next game, tap{' '}
          <strong>Share</strong>, then <strong>Add to Home Screen</strong>, and start or join
          games from inside the app. Keep playing this game here.
        </Text>
      </Alert>
    );
  }

  if (!pushSupported() || notificationPermission() === 'denied') return null;

  return (
    <Group justify="center">
      <Button
        variant="light"
        leftSection={<IconBell size={16} />}
        loading={loading}
        onClick={handleEnable}
      >
        Notify me when it&apos;s my turn
      </Button>
      {error && (
        <Text size="xs" c="red">
          {error}
        </Text>
      )}
    </Group>
  );
};

export default PushNotificationPrompt;
